import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useFormikContext } from 'formik';
import {
  CircularProgress,
  IconButton,
  TableRow,
  TableCell,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import classNames from 'classnames';

import { ILeaderboardScores } from 'features/admin/types';

import EditableInput from './EditableInput';

interface IProps {
  loading: boolean;
  index: number;
  editIndex: number;
  onChange: (index: number) => void;
}

const useStyles = makeStyles({
  disabled: {
    opacity: 0.4,
  },
  loading: {
    paddingLeft: 10,
    paddingRight: 10,
  },
});

const EditableRow: FC<IProps> = props => {
  const { loading, index, editIndex, onChange } = props;
  const { values, setFieldValue, initialValues } = useFormikContext<ILeaderboardScores>();
  const { t } = useTranslation();
  const classes = useStyles();

  const { event, score } = values.eventScores[index];
  const isEditRow = index === editIndex;
  const isEditing = editIndex >= 0;
  const isDisabled = !isEditRow && isEditing;
  const className = classNames({
    [classes.disabled]: isDisabled,
  });

  function handleCancel() {
    onChange(-1);
    setFieldValue(`eventScores.${index}.score`, initialValues.eventScores[index].score);
  }

  function renderEditAction() {
    return (
      <Tooltip enterDelay={1000} title={t('common:edit')} placement="bottom">
        <IconButton component="span" disabled={isDisabled} onClick={() => onChange(index)}>
          <EditIcon />
        </IconButton>
      </Tooltip>
    );
  }

  function renderSaveCancelAction() {
    return (
      <Fragment>
        {loading && (
          <IconButton className={classes.loading} disabled>
            <CircularProgress color="inherit" size={20} />
          </IconButton>
        )}
        {!loading && (
          <Tooltip enterDelay={1000} title={t('common:save')} placement="bottom">
            <IconButton type="submit">
              <CheckIcon color="action" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip enterDelay={1000} title={t('common:cancel')} placement="bottom">
          <IconButton component="span" onClick={handleCancel}>
            <CloseIcon color="action" />
          </IconButton>
        </Tooltip>
      </Fragment>
    );
  }

  function renderEditableInput() {
    return <Field name={`eventScores[${index}].score`} component={EditableInput} />;
  }

  function renderScore() {
    return <span onClick={() => onChange(index)}>{score}</span>;
  }

  return (
    <TableRow key={event} className={className}>
      <TableCell>{isEditRow ? renderSaveCancelAction() : renderEditAction()}</TableCell>
      <TableCell>
        <Typography variant="body2" color={isEditRow ? 'textPrimary' : 'textSecondary'}>
          {t(`leaderboard:${event}`)}
        </Typography>
      </TableCell>
      <TableCell>{isEditRow ? renderEditableInput() : renderScore()}</TableCell>
    </TableRow>
  );
};

export default EditableRow;
